import { useEffect, useMemo, useState } from 'react';
import { Download, FileText, Search, Trash2, CreditCard } from '@/components/icons';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { StatusBadge, Badge } from '@/components/ui/Badge';
import { SlaBadge } from '@/components/ui/SlaBadge';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { usePrompt } from '@/components/ui/usePrompt';
import { HandoffModal } from '@/components/ui/HandoffModal';
import { useAuth } from '@/auth/useAuth';
import {
  acknowledgePo,
  cancelPo,
  deletePo,
  listPos,
  poExportPath,
  markPoPaid,
  type Po,
} from '@/api/po';
import { getSettings } from '@/api/admin';
import { apiError } from '@/lib/apiError';
import { downloadExcel } from '@/lib/downloadExcel';
import { formatDate, formatINRSymbol } from '@/lib/format';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { PoFormModal } from './PoFormModal';
import { PoSendModal } from './PoSendModal';
import { PoFromPrModal } from './PoFromPrModal';

const PAY_TONE: Record<Po['payStatus'], 'muted' | 'warning' | 'success'> = {
  Pending: 'muted',
  Partial: 'warning',
  Paid: 'success',
};

export function PoPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const ask = usePrompt();
  const navigate = useNavigate();
  const docViewer = useDocViewer();
  const { hasPerm } = useAuth();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // PO date window — All / Date (from–to) / Month / Quarter / Year.
  const period = usePeriod();
  const { range } = period;
  useEffect(() => setPage(1), [range]);
  const [modalOpen, setModalOpen] = useState(false);
  const [fromPrOpen, setFromPrOpen] = useState(false);
  const [editing, setEditing] = useState<Po | null>(null);
  const [sendFor, setSendFor] = useState<Po | null>(null);
  const [handoffFor, setHandoffFor] = useState<Po | null>(null);

  // Fast-track (PR → PO, skipping RFQ + Quote) is offered when express mode is on.
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings, staleTime: 300_000 });
  const mode = settings?.procurementMode ?? 'both';
  const expressOn = mode === 'express' || mode === 'both';
  const fullOn = mode === 'full' || mode === 'both';

  const filter = useMemo(
    () => ({
      status: status || undefined,
      search: search || undefined,
      from: range?.from,
      to: range?.to,
      page,
      pageSize,
    }),
    [status, search, range, page, pageSize],
  );

  // Export every record matching the filters + search (server-side, no page limit).
  function onExport() {
    downloadExcel(poExportPath(filter), 'GRHAMOY_Purchase_Orders.xlsx', 120_000)
      .catch((err) => toast.push(apiError(err, 'Export failed'), 'error'));
  }

  const query = useQuery({
    queryKey: ['po', filter],
    queryFn: () => listPos(filter),
    placeholderData: keepPreviousData,
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['po'] });
  }

  const ackMutation = useMutation({
    mutationFn: acknowledgePo,
    onSuccess: (po) => {
      invalidate();
      toast.pushStep(`PO ${po.poNo} acknowledged`, {
        body: 'Next step — record the GRN when the goods arrive.',
        href: `/purchase/grn?po=${po.id}`,
        nextLabel: 'Open GRN',
        tag: `po-ack-${po.id}`,
      });
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Acknowledge failed'), 'error'),
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => cancelPo(id, reason),
    onSuccess: () => { invalidate(); toast.push('PO cancelled', 'info'); },
    onError: (e: unknown) => toast.push(apiError(e, 'Cancel failed'), 'error'),
  });

  const delMutation = useMutation({
    mutationFn: deletePo,
    onSuccess: () => { invalidate(); toast.push('PO deleted', 'info'); },
    onError: (e: unknown) => toast.push(apiError(e, 'Delete failed'), 'error'),
  });

  const payMutation = useMutation({
    mutationFn: ({ id, amount, mode, ref }: { id: number; amount: number; mode: string; ref?: string }) =>
      markPoPaid(id, { amount, mode, ref }),
    onSuccess: () => { invalidate(); toast.push('Payment recorded', 'success'); },
    onError: (e: unknown) => toast.push(apiError(e, 'Payment record failed'), 'error'),
  });

  async function onDelete(p: Po) {
    if (!await confirmDlg(`Delete ${p.poNo}? This cannot be undone.`)) return;
    delMutation.mutate(p.id);
  }
  async function onCancel(p: Po) {
    const reason = await ask({ title: `Cancel ${p.poNo}?`, label: 'Reason', multiline: true, tone: 'danger', confirmLabel: 'Cancel PO' });
    if (!reason?.trim()) return;
    cancelMutation.mutate({ id: p.id, reason });
  }
  async function onPay(p: Po) {
    const amountText = await ask({
      title: `Record payment — ${p.poNo}`,
      message: `Grand Total ₹${p.grandTotal}`,
      label: 'Amount (₹)',
      inputMode: 'decimal',
      confirmLabel: 'Next',
    });
    if (amountText === null) return;
    const amount = Number(amountText);
    if (!Number.isFinite(amount) || amount <= 0) return;
    const mode = (await ask({ title: `Record payment — ${p.poNo}`, label: 'Mode', placeholder: 'e.g. Bank Transfer, Cheque, UPI', defaultValue: 'Bank Transfer', required: false, confirmLabel: 'Next' })) ?? 'Bank Transfer';
    const ref = (await ask({ title: `Record payment — ${p.poNo}`, label: 'Reference', placeholder: 'Optional', required: false, confirmLabel: 'Record payment' })) ?? undefined;
    payMutation.mutate({ id: p.id, amount, mode, ref: ref || undefined });
  }
  function onGrn(p: Po) {
    // If the current user can enter a GRN, take them straight there.
    // Otherwise open the handoff modal so they can forward the request
    // to whoever owns the GRN module (typically Store Manager / Keeper).
    if (hasPerm('grn')) {
      navigate(`/purchase/grn?po=${p.id}`);
    } else {
      setHandoffFor(p);
    }
  }

  const data = query.data?.data ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div>
      <SectionHeader
        title="Purchase Orders"      />

      <FilterBar>
        <FilterField label="Search" className="sm:min-w-[220px]">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search"
              className="!pl-5 sm:!pl-8"
              aria-label="Search POs"
            />
          </div>
        </FilterField>
        <FilterField label="Status">
          <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option>Draft</option>
            <option>Sent</option>
            <option>Acknowledged</option>
            <option>Delivered</option>
            <option>Cancelled</option>
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        {/* Create chips live inside the filter block on both viewports. */}
        {expressOn && (
          <button
            type="button"
            onClick={() => setFromPrOpen(true)}
            title="Fast-track: generate a Draft PO from an approved PR (skips RFQ + Quote Approval)"
            className="inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-accent bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
          >
            ＋ New PO from PR
          </button>
        )}
        {fullOn && (
          <button
            type="button"
            onClick={() => { setEditing(null); setModalOpen(true); }}
            className={
              'inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:h-8 sm:self-end sm:px-3 sm:text-xs' +
              (expressOn ? '' : ' sm:ml-auto')
            }
          >
            ＋ New PO
          </button>
        )}
        <button
          type="button"
          onClick={onExport}
          className="inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:self-end sm:px-3 sm:text-xs"
          title="Export every PO matching these filters to Excel"
        >
          <Download size={12} /> Export
        </button>
      </FilterBar>

      <Table<Po>
        storageKey="po"
        title="Purchase Orders"
        count={`Showing ${data.length} of ${total}`}
        rows={data}
        rowKey={(r) => r.id}
        loading={query.isFetching && data.length === 0}
        columns={[
          { key: 'poNo', header: 'PO #', cell: (r) => <span className="font-extrabold text-accent">{r.poNo}</span> },
          { key: 'date', header: 'Date', cell: (r) => formatDate(r.poDate) },
          { key: 'subject', header: 'Subject', width: '200px', cell: (r) => <span className="block max-w-[200px] truncate" title={r.subject ?? undefined}>{r.subject || '—'}</span> },
          { key: 'vendor', header: 'Vendor', cell: (r) => <span className="font-bold">{r.vendor.name}</span> },
          { key: 'rfq', header: 'RFQ', cell: (r) => (r.rfq?.rfqNo ? <span className="text-[11px] text-[#a78bfa]">{r.rfq.rfqNo}</span> : '—') },
          {
            key: 'source',
            header: 'Price Source',
            align: 'center',
            cell: (r) => {
              const ps = r.priceSource ?? 'Manual';
              const tone =
                ps === 'Quoted'
                  ? 'bg-success/15 text-success'
                  : ps === 'Express'
                  ? 'bg-accent/15 text-accent'
                  : 'bg-[var(--border-color)] text-[var(--text-muted)]';
              return (
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${tone}`}
                  title={
                    ps === 'Quoted'
                      ? 'Priced from an approved vendor quote'
                      : ps === 'Express'
                      ? 'Fast-tracked from an approved PR, prices prefilled from the PR (no quote)'
                      : 'Standalone PO, priced manually'
                  }
                >
                  {ps}
                </span>
              );
            },
          },
          { key: 'delivery', header: 'Delivery', cell: (r) => formatDate(r.deliveryDate ?? '') || '—' },
          { key: 'payTerm', header: 'Pay Terms', align: 'right', cell: (r) => `${r.paymentTermDays}d` },
          { key: 'grnCount', header: 'GRNs', align: 'right', cell: (r) => r.grnCount ?? 0 },
          { key: 'amount', header: 'Grand Total', align: 'right', cell: (r) => formatINRSymbol(r.grandTotal) },
          { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
          { key: 'pay', header: 'Pay', cell: (r) => <Badge tone={PAY_TONE[r.payStatus]}>{r.payStatus}</Badge> },
          { key: 'sla', header: 'SLA', cell: (r) => <SlaBadge sla={r.sla ?? null} /> },
          {
            key: 'actions',
            header: '',
            width: '240px',
            cell: (r) => (
              <div className="flex items-center gap-1">
                <Button variant="glass" size="sm" onClick={() => { setEditing(r); setModalOpen(true); }}>View</Button>
                <Button variant="glass" size="sm" onClick={() => docViewer.open('po', r.id, r.poNo)} title="Open A4 document"><FileText size={14} /></Button>
                {r.status === 'Draft' && (
                  <>
                    <Button variant="primary" size="sm" onClick={() => setSendFor(r)}>Send</Button>
                    <Button variant="glass" size="sm" onClick={() => onDelete(r)} title="Delete"><Trash2 size={14} /></Button>
                  </>
                )}
                {r.status === 'Sent' && (
                  <Button variant="success" size="sm" onClick={() => ackMutation.mutate(r.id)}>Acknowledge</Button>
                )}
                {(r.status === 'Sent' || r.status === 'Acknowledged') && (
                  <>
                    <Button variant="glass" size="sm" onClick={() => onGrn(r)}>+ GRN</Button>
                    <Button variant="danger" size="sm" onClick={() => onCancel(r)}>Cancel</Button>
                  </>
                )}
                {(r.status === 'Acknowledged' || r.status === 'Delivered') && r.payStatus !== 'Paid' && (
                  <Button variant="glass" size="sm" onClick={() => onPay(r)}><CreditCard size={12} className="mr-1 inline-block align-[-2px]" /> Pay</Button>
                )}
              </div>
            ),
          },
        ]}
        footer={<Pagination page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />}
      />

      <PoFormModal open={modalOpen} editing={editing} onClose={() => setModalOpen(false)} />
      <PoFromPrModal open={fromPrOpen} onClose={() => setFromPrOpen(false)} />
      <PoSendModal open={sendFor !== null} po={sendFor} onClose={() => { setSendFor(null); invalidate(); }} />
      {handoffFor && (
        <HandoffModal
          open
          onClose={() => setHandoffFor(null)}
          action="record a GRN against this Purchase Order"
          payload={{
            moduleId: 'grn',
            subject: `GRN needed for ${handoffFor.poNo}`,
            body: `Please record a GRN against PO ${handoffFor.poNo} (${handoffFor.vendor.name}). Delivery expected: ${handoffFor.deliveryDate ?? 'not set'}.`,
            entityType: 'po',
            entityId: handoffFor.id,
            actionHref: `/purchase/grn?po=${handoffFor.id}`,
          }}
        />
      )}
    </div>
  );
}
